import { FC } from 'react';
import { Check, Calendar, Clock, type LucideIcon } from 'lucide-react';
import Modal from '@new/common/ui/Modal/Modal';
import { Button } from '@new/common/ui';
import { timestampToDate, formatDate, formatTime } from '@new/common/lib/dateTime';
import { type ScheduledCall } from '@teachur/http-client';

interface ScheduleSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  scheduledCall?: ScheduledCall;
}

interface DetailItemProps {
  icon: LucideIcon;
  label: string;
  value: string;
}

const DetailItem: FC<DetailItemProps> = ({ icon: Icon, label, value }) => (
  <div className="flex flex-1 flex-col justify-start items-center gap-2">
    <div className="flex items-center justify-center gap-2 w-full">
      <Icon className="w-5 h-5 text-(--neutral-black-NB600)!" />
      <span className="[font-size:var(--fs-l5)]! lg:[font-size:var(--fs-p3)]! font-semibold text-(--neutral-black-NB800)!">
        {label}
      </span>
    </div>
    <p className="[font-size:var(--fs-l5)]! lg:[font-size:var(--fs-p3)]! leading-tight text-(--neutral-black-NB600)! m-0!">
      {value}
    </p>
  </div>
);

export const ScheduleSuccessModal: FC<ScheduleSuccessModalProps> = ({
  isOpen,
  onClose,
  scheduledCall,
}) => {
  if (!scheduledCall) return null;

  const callDate = timestampToDate(scheduledCall.startTime);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="flex flex-col items-center text-center gap-8">
        <div className="flex flex-col items-center gap-8">
          <div className="w-10 lg:w-14 h-10 lg:h-14 rounded-xl bg-(--success-S200) flex items-center justify-center">
            <Check className="w-6 lg:w-8 h-6 lg:h-8 text-(--success-S600)" strokeWidth={2} />
          </div>
          <div className="flex flex-col gap-2">
            <h4 className="[font-size:var(--fs-h6)]! lg:[font-size:var(--fs-h4)]! text-(--neutral-black-NB1000)! font-bold m-0!">
              Successfully Requested!
            </h4>
            <p className="[font-size:var(--fs-p4)]! lg:[font-size:var(--fs-p2)]! text-(--neutral-black-NB900)! m-0!">
              Congratulations on requesting a Course Hearing! Once an Instructor confirms your
              request, the video link will appear for you to join at the appointed time.{' '}
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-5 w-full">
          <div className="w-full bg-(--neutral-black-NB100) rounded-xl p-4">
            <div className="flex">
              <DetailItem icon={Calendar} label="Date" value={formatDate(callDate)} />
              <DetailItem icon={Clock} label="Time" value={formatTime(callDate)} />
            </div>
          </div>

          <div className="w-full bg-(--warning-W100) rounded-lg p-4">
            <p className="[font-size:var(--fs-p4)]! lg:[font-size:var(--fs-p3)]! text-left leading-tight text-(--warning-W600)! m-0!">
              A confirmation email has been sent to your email address.
            </p>
          </div>
        </div>

        <Button variant="primary" onClick={onClose} className="w-full">
          Done
        </Button>
      </div>
    </Modal>
  );
};
